import './style.modules.scss'

import { StockQuoteService } from '@ampcore/client-side-services'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { ISSUER_NAME_MAP, MARKET_STATUS } from './constants'
import { formatDisplayPrice, formatPriceChange } from './formatters'
import {
  DividendProps,
  MarketDataItem,
  MarketWatchProps
} from './MarketWatch.types'
import { getLastUpdatedTimestamp } from './timestamp'

/**
 * Parses a comma-separated list of symbols into unique, trimmed values.
 */
const parseSymbols = (listCusipd: string): string[] => {
  const symbols = listCusipd
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
  return [...new Set(symbols)]
}

/**
 * Determines the CSS class and arrow icon for a price change direction.
 */
const getPriceDirection = (prcChg: string) => {
  const value = Number.parseFloat(prcChg)
  if (value > 0) {
    return {
      colorClass: 'u-textColorConfirm',
      arrow: 'arrow_upward'
    }
  }
  if (value < 0) {
    return {
      colorClass: 'u-textColorAlert',
      arrow: 'arrow_downward'
    }
  }
  return { colorClass: '', arrow: '' }
}

/**
 * Builds disclaimer HTML based on market data state.
 */
const buildDisclaimerHtml = (
  data: MarketDataItem[],
  dividend?: DividendProps,
  previousPriceDisclaimer?: string
): string => {
  let message = ''
  let prevCloseMessage = ''

  for (const item of data) {
    if (Number.parseFloat(item.quoteInfo.prcChg) === 0) {
      prevCloseMessage = previousPriceDisclaimer ?? ''
    }
    if (item.dividend !== null) {
      if (item.dividend.pendExDivdInd === 'PendingExDividend' && dividend) {
        message = `<p><span class="stock-dividend-symbol">${dividend.preDividendSymbol}</span>${dividend.preDividendDisclaimer}</p>`
      }
      if (item.dividend.exDivdInd === 'TradingExDividend' && dividend) {
        message = `<p><span class="stock-dividend-symbol">${dividend.exDividendSymbol}</span>${dividend.exDividendDisclaimer}</p>`
      }
    }
  }

  return prevCloseMessage + message
}

export const MarketWatch = (props: MarketWatchProps) => {
  const {
    listCusipd,
    disclaimer = '',
    errorHeading,
    errorMessage,
    previousCloseLabel,
    previousPriceDisclaimer,
    unChangeLabel,
    dividend,
    testId
  } = props

  const [loading, setLoading] = useState(true)
  const [marketData, setMarketData] = useState<MarketDataItem[]>([])
  const [lastUpdated, setLastUpdated] = useState('')
  const [error, setError] = useState<string | null>(null)

  const symbols = useMemo(() => parseSymbols(listCusipd), [listCusipd])

  const fetchQuotes = useCallback(async () => {
    if (!symbols.length) {
      setLoading(false)
      return
    }

    const service = new StockQuoteService()
    const result = await service.getQuotes({
      data: { cusip: [], symbol: symbols }
    })

    if (!result) {
      setError('Unable to fetch quotes')
      setLoading(false)
      return
    }

    if (result.message === MARKET_STATUS.SUCCESS) {
      const response = result.response as MarketDataItem[]
      if (response?.length === symbols.length) {
        setMarketData(response)
        setLastUpdated(getLastUpdatedTimestamp(response))
      }
    } else {
      setError(result.message)
    }

    setLoading(false)
  }, [symbols])

  useEffect(() => {
    fetchQuotes()
  }, [fetchQuotes])

  if (loading) {
    return (
      <div
        className="u-posRelative u-marginCenter u-padding48"
        data-testid={testId ? `${testId}-loading` : undefined}
      >
        <div className="Loading u-posAbsoluteCenter u-textColorBrand">
          <div className="Loading-indicator"></div>
          <div className="Loading-indicator"></div>
          <div className="Loading-indicator"></div>
        </div>
      </div>
    )
  }

  if (error || !marketData.length) {
    return (
      <div
        className="u-posRelative u-marginCenter u-padding24"
        data-testid={testId ? `${testId}-error` : undefined}
      >
        <h3>{errorHeading}</h3>
        {errorMessage && (
          <div
            className="u-paddingTop8 u-floatLeft"
            dangerouslySetInnerHTML={{ __html: errorMessage }}
          />
        )}
      </div>
    )
  }

  const qualityOfService = marketData.at(-1)?.quoteInfo?.qltyOfServ
  const disclaimerHtml = `<div id="Disclaimer-Text">${disclaimer}${buildDisclaimerHtml(
    marketData,
    dividend,
    previousPriceDisclaimer
  )}</div>`

  return (
    <table className="Table" id="stockQuote" data-testid={testId}>
      <colgroup>
        <col className="u-size2of12" />
        <col className="u-size4of12" />
        <col className="u-size2of12" />
      </colgroup>
      <tbody className="Table-body">
        <tr className="Table-row">
          <td
            className="Table-cell u-textColorLight u-paddingBottom8 u-textSizeMsrm1"
            colSpan={3}
            data-testid={testId ? `${testId}-quality` : undefined}
          >
            As of {lastUpdated} ({qualityOfService})
          </td>
        </tr>
        {marketData.map(item => {
          const { prcChg, lstTrdPrc, open } = item.quoteInfo
          const direction = getPriceDirection(prcChg)
          const price = formatDisplayPrice(lstTrdPrc)
          const priceChange = formatPriceChange(prcChg)
          const isUnchanged = Number.parseFloat(prcChg) === 0
          const isBeforeOpen = isUnchanged && Number.parseFloat(open) === 0
          const cleanSymbol = item.symbol
            .replace(/[^a-zA-Z0-9]/g, '')
            .toLowerCase()

          let divSymbol: React.ReactNode = null
          if (item.dividend?.pendExDivdInd === 'PendingExDividend') {
            divSymbol = (
              <span
                className="stock-dividend-symbol"
                dangerouslySetInnerHTML={{
                  __html: dividend?.preDividendSymbol ?? ''
                }}
              />
            )
          } else if (item.dividend?.exDivdInd === 'TradingExDividend') {
            divSymbol = (
              <span
                className="stock-dividend-symbol"
                dangerouslySetInnerHTML={{
                  __html: dividend?.exDividendSymbol ?? ''
                }}
              />
            )
          }

          const cellClass = direction.colorClass
            ? `Table-cell ${direction.colorClass}`
            : 'Table-cell'

          return (
            <tr className={`Table-row row-${cleanSymbol}`} key={cleanSymbol}>
              <td
                className={`Table-cell cell-${cleanSymbol} u-textColorDefault u-textNoWrap u-textSizeMsrm1`}
              >
                {ISSUER_NAME_MAP[item.description.issuerNm] ??
                  item.description.issuerNm}
              </td>
              <td className="Table-cell u-textSizeMsrm1 u-textRight u-textColorDefault u-textNoWrap u-paddingRight4">
                {divSymbol}
                {isBeforeOpen && (
                  <span className="js-pc">
                    <strong>{previousCloseLabel} </strong>
                  </span>
                )}
                {price}{' '}
                {direction.arrow && (
                  <span
                    className={`adel-material-symbols-16 stock-arrow ${direction.colorClass}`}
                    role="img"
                  >
                    {direction.arrow}
                  </span>
                )}
              </td>
              <td className={`${cellClass} u-textRight`}>
                {isUnchanged ? (
                  <span className="js-price-change">
                    <strong>{unChangeLabel}</strong>
                  </span>
                ) : (
                  <span className="js-price-change">{priceChange}</span>
                )}
              </td>
            </tr>
          )
        })}
        <tr className="Table-row js-row-disclaimer">
          <td
            className="Table-cell"
            colSpan={3}
            dangerouslySetInnerHTML={{ __html: disclaimerHtml }}
          ></td>
        </tr>
      </tbody>
    </table>
  )
}
